// Handles Supabase auth redirects after the user clicks an email link
// (signup confirmation, magic link, password reset). The flow Supabase uses
// today is PKCE: the redirect URL carries ?code=XXX which we exchange for
// a real session via supabase.auth.exchangeCodeForSession().
//
// Older implicit flow puts tokens in the hash (#access_token=...) — we
// handle that too as a fallback.
//
// On success: brief "Email verified" beat, then bounce to "/" so the rest
// of App.js loads with the new session and continues the signup wizard /
// dashboard normally.
//
// On failure: clear message + buttons so the user can recover. Typos in
// emails surface here because the token never arrives and they're forced
// to start over with the correct address.

import { useEffect, useState } from 'react';
import { supabase } from './supabase';

const ORANGE = '#F05A28';
const BLACK = '#231F20';

export default function AuthCallback() {
  const [status, setStatus] = useState('working'); // 'working' | 'success' | 'error'
  const [errorMsg, setErrorMsg] = useState('');
  const [signupEmail, setSignupEmail] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const hashStr = (window.location.hash || '').replace(/^#/, '');
        const hashParams = new URLSearchParams(hashStr);

        // Surface any Supabase error in the URL FIRST — no point exchanging
        // a code that already errored out (expired/used/wrong project).
        const errParam = params.get('error') || hashParams.get('error');
        const errDesc = params.get('error_description') || hashParams.get('error_description');
        if (errParam) {
          if (cancelled) return;
          setStatus('error');
          // Friendlier wording for the common case
          if (/expired/i.test(errDesc || '') || errParam === 'access_denied') {
            setErrorMsg('This link is no longer valid. It may have expired, already been used, or been opened by your email provider\'s safety scanner before you could click it.');
          } else {
            setErrorMsg(errDesc || errParam);
          }
          return;
        }

        // PKCE flow: ?code=...
        const code = params.get('code');
        if (code) {
          const { data, error } = await supabase.auth.exchangeCodeForSession(code);
          if (cancelled) return;
          if (error) {
            setStatus('error');
            setErrorMsg(
              /expired|invalid/i.test(error.message || '')
                ? 'This verification link is no longer valid. It may have expired or already been used.'
                : (error.message || 'Something went wrong verifying your email.')
            );
            return;
          }
          setSignupEmail(data?.user?.email || null);
          setStatus('success');
          return;
        }

        // Implicit/hash flow: #access_token=...&refresh_token=...
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');
        if (accessToken && refreshToken) {
          const { data, error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });
          if (cancelled) return;
          if (error) {
            setStatus('error');
            setErrorMsg(error.message || 'Could not establish your session.');
            return;
          }
          setSignupEmail(data?.user?.email || null);
          setStatus('success');
          return;
        }

        // Nothing actionable on the URL — likely an old reload or someone
        // hit the route directly.
        if (cancelled) return;
        setStatus('error');
        setErrorMsg("This page only works when opened from a Work Van email link. If you're trying to log in, head to the home page.");
      } catch (e) {
        if (cancelled) return;
        setStatus('error');
        setErrorMsg(e?.message || 'Unexpected error while verifying.');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // On success, clear the messy URL and bounce home after a brief beat so
  // App.js picks up the new session and routes the user to wherever they
  // belong (signup wizard for new users, dashboard for returning).
  useEffect(() => {
    if (status !== 'success') return;
    const t = setTimeout(() => {
      window.location.replace('/');
    }, 1400);
    return () => clearTimeout(t);
  }, [status]);

  return (
    <div style={{
      minHeight: '100vh',
      background: '#0a0a0a',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem 1rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    }}>
      <div style={{
        background: '#1a1a1a',
        border: `1px solid ${status === 'error' ? '#e74c3c33' : '#2a2a2a'}`,
        borderRadius: 16,
        padding: '2rem 1.75rem',
        maxWidth: 440,
        width: '100%',
        textAlign: 'center',
        color: '#e0e0e0',
      }}>
        <img
          src="/WorkVan-logo-dark.png"
          alt="Work Van"
          style={{ height: 36, marginBottom: '1.5rem', objectFit: 'contain' }}
        />

        {status === 'working' && (
          <>
            <div style={{
              width: 48, height: 48, borderRadius: '50%',
              border: `3px solid ${ORANGE}`, borderTopColor: 'transparent',
              margin: '0 auto 1rem', animation: 'spin 0.8s linear infinite',
            }} />
            <h2 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700 }}>Verifying your email…</h2>
            <p style={{ color: '#888', fontSize: '0.9rem', marginTop: '0.5rem' }}>One sec.</p>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </>
        )}

        {status === 'success' && (
          <>
            <div style={{
              width: 56, height: 56, borderRadius: '50%',
              background: '#10B98120', color: '#10B981',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 1rem', fontSize: 28, fontWeight: 700,
            }}>✓</div>
            <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700 }}>Email verified!</h2>
            <p style={{ color: '#888', fontSize: '0.9rem', marginTop: '0.5rem' }}>
              {signupEmail ? <>You're signed in as <strong style={{ color: '#e0e0e0' }}>{signupEmail}</strong>.</> : "You're signed in."}
              <br />
              Taking you to your account…
            </p>
          </>
        )}

        {status === 'error' && (
          <>
            <div style={{
              width: 56, height: 56, borderRadius: '50%',
              background: '#e74c3c20', color: '#e74c3c',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 1rem', fontSize: 28, fontWeight: 700,
            }}>!</div>
            <h2 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 700 }}>We couldn't verify that link</h2>
            <p style={{ color: '#aaa', fontSize: '0.9rem', marginTop: '0.6rem', lineHeight: 1.55 }}>
              {errorMsg}
            </p>
            <p style={{ color: '#666', fontSize: '0.8rem', marginTop: '0.85rem' }}>
              If you typed your email wrong during signup, head back to the home page and sign up again with the correct address.
            </p>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', marginTop: '1.25rem', flexWrap: 'wrap' }}>
              <a
                href="/"
                style={{
                  padding: '0.65rem 1.2rem',
                  background: ORANGE,
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  textDecoration: 'none',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                }}
              >
                Back to home
              </a>
              <a
                href="mailto:support@workvanapp.com"
                style={{
                  padding: '0.65rem 1.2rem',
                  background: 'transparent',
                  color: '#aaa',
                  border: '1px solid #3a3a3a',
                  borderRadius: 8,
                  textDecoration: 'none',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                }}
              >
                Email support
              </a>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
